import { Box, Chip, Divider, Grid, Stack, Typography, alpha } from "@mui/material";
import dayjs from "dayjs";

import { useGetOrdenTrabajo } from "@/shared/actions/erp";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { OrdenTrabajo } from "@/shared/interfaces";
import FotosTrasladoTab from "./FotosTrasladoTab";

const ACCENT = "#1565c0";

function fmt(v?: string | null): string {
  return v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—";
}

function duracion(inicio?: string | null, fin?: string | null): string {
  if (!inicio || !fin) return "—";
  const mins = dayjs(fin).diff(dayjs(inicio), "minute");
  if (mins < 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function Dato({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value ?? "—"}
      </Typography>
    </Grid>
  );
}

type ResumenTrasladoTecnicoModalProps = {
  open: boolean;
  onClose: () => void;
  ordenTrabajo?: OrdenTrabajo;
  /**
   * Solo para el TÉCNICO: permite volver a subir las fotos que Activaciones
   * haya eliminado (no permite eliminar ni reemplazar). Por defecto el resumen
   * es de solo lectura.
   */
  permitirCompletar?: boolean;
};

/**
 * Resumen de lo que hizo el técnico en un traslado finalizado: tiempos,
 * coordenadas y las 3 galerías de fotos (ingreso/proceso/finalización). Las
 * fotos se actualizan en vivo. Con `permitirCompletar` el técnico puede subir
 * las fotos que Activaciones eliminó.
 */
export default function ResumenTrasladoTecnicoModal({
  open,
  onClose,
  ordenTrabajo,
  permitirCompletar = false,
}: ResumenTrasladoTecnicoModalProps) {
  // Traemos la OT completa por id (la fila de la tabla puede venir recortada).
  const { data } = useGetOrdenTrabajo(ordenTrabajo?.id_orden_trabajo || "");
  const ot = (((data as any)?.data as OrdenTrabajo) ?? ordenTrabajo) as any;

  const cliente =
    ot?.contrato_data?.razon_social ||
    ot?.prospecto_data?.razon_social ||
    ot?.numero_referencia ||
    "—";

  return (
    <ScrollableDialogProps
      open={open}
      title="Resumen del traslado — Técnico"
      minWidth="70%"
      cancelTextBtn="Cerrar"
      onCloseIcon={onClose}
      onClose={onClose}
      contentNode={
        <Box sx={{ p: 1 }}>
          {/* Encabezado */}
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: alpha(ACCENT, 0.03),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {cliente}
              </Typography>
              <Chip
                size="small"
                label={ot?.estado_orden_trabajo || "—"}
                color="success"
                variant="outlined"
              />
            </Stack>
            <Grid container spacing={2}>
              <Dato label="Inicio del traslado" value={fmt(ot?.fecha_hora_inicio_instalacion)} />
              <Dato label="Fin del traslado" value={fmt(ot?.fecha_hora_fin_instalacion)} />
              <Dato
                label="Duración"
                value={
                  <Chip
                    size="small"
                    label={duracion(ot?.fecha_hora_inicio_instalacion, ot?.fecha_hora_fin_instalacion)}
                    sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, fontWeight: 700 }}
                  />
                }
              />
              <Dato label="Coordenadas" value={ot?.coordenadas || "—"} />
            </Grid>
          </Box>

          {/* Evidencia fotográfica (solo lectura, o "completar" para el técnico) */}
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: ACCENT, mb: 1 }}>
            Evidencia fotográfica
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <FotosTrasladoTab ordenTrabajo={ot} readOnly={!permitirCompletar} />
        </Box>
      }
    />
  );
}
